import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '../stores/auth.js'
import { useOrgStore } from '../stores/orgStore.js'
import { assertPermission, RBAC_ERROR } from '../utils/permissions.js'
import { isActiveOrgInvalidated, isActiveSubOrgInvalidated } from '../utils/lifecycleUtils.js'

const routes = [
  {
    path: '/login',
    name: 'login',
    component: () => import('../views/LoginView.vue'),
    meta: { title: 'Login' },
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: () => import('../views/ForgotPasswordView.vue'),
  },
  {
    path: '/reset-password',
    name: 'reset-password',
    component: () => import('../views/ResetPasswordView.vue'),
  },
  {
    path: '/change-password',
    name: 'change-password',
    component: () => import('../views/ChangePasswordView.vue'),
  },
  {
    path: '/register',
    name: 'register',
    component: () => import('../views/RegisterView.vue'),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/dashboard',
    name: 'dashboard',
    component: () => import('../views/DashboardView.vue'),
    meta: {
      requiresAuth: true,
      permission: 'viewLotteries',
      title: 'Dashboard',
    },
  },
  {
    path: '/lotteries',
    name: 'lottery-admin',
    component: () => import('../views/LotteryAdminView.vue'),
    meta: {
      requiresAuth: true,
      permission: 'viewLotteries',
      title: 'Lotteries',
    },
  },
  {
    path: '/lottery/:id',
    name: 'lottery-detail',
    component: () => import('../views/LotteryDetailView.vue'),
    meta: {
      requiresAuth: true,
      permission: 'viewLotteries',
      title: 'Lottery Detail',
    },
  },
  {
    path: '/users',
    name: 'users',
    component: () => import('../views/UsersView.vue'),
    meta: {
      requiresAuth: true,
      permission: 'manageUsers',
      title: 'Users',
    },
  },
  {
    path: '/seller',
    name: 'seller-dashboard',
    component: () => import('../views/SellerDashboardView.vue'),
    meta: {
      requiresAuth: true,
      permission: 'viewBatches',
      title: 'Seller Dashboard',
    },
  },
  {
    path: '/public/lookup',
    name: 'public-lookup',
    component: () => import('../views/PublicTicketLookupView.vue'),
    meta: {
      title: 'Ticket Lookup',
    },
  },
  {
    path: '/public/lottery/:id/results',
    name: 'public-lottery-results',
    component: () => import('../views/PublicLotteryResultView.vue'),
    meta: {
      title: 'Public Draw Result',
    },
  },
  {
    path: '/settings/profile',
    name: 'settings-profile',
    component: () => import('../views/SettingsProfileView.vue'),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/settings/security',
    name: 'settings-security',
    component: () => import('../views/SettingsSecurityView.vue'),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/admin/orgs',
    name: 'admin-orgs',
    component: () => import('../views/SuperAdminPanelView.vue'),
    meta: {
      requiresAuth: true,
      requiresSuperAdmin: true,
      title: 'Organizations',
    },
  },
  {
    path: '/admin/suborgs',
    name: 'admin-suborgs',
    component: () => import('../views/SubOrgAdminView.vue'),
    meta: {
      requiresAuth: true,
      requiresSuperAdmin: false,
      bypassLifecycleGuard: true,
      title: 'Suborganizations',
    },
  },
  {
    path: '/admin/smtp',
    name: 'admin-smtp',
    component: () => import('../views/AdminSMTPView.vue'),
    meta: {
      requiresAuth: true,
      requiresSuperAdmin: true,
      title: 'SMTP Settings',
    },
  },
  {
    path: '/admin/audit-logs',
    name: 'admin-audit-logs',
    component: () => import('../views/AuditLogView.vue'),
    meta: {
      requiresAuth: true,
      requiresSuperAdmin: true,
      title: 'Audit Timeline',
    },
  },
  {
    path: '/admin/operations',
    name: 'admin-operations',
    component: () => import('../views/OperationsOverviewView.vue'),
    meta: {
      requiresAuth: true,
      requiresSuperAdmin: true,
      title: 'Operations Overview',
    },
  },
  {
    path: '/admin/financial-overview',
    name: 'admin-financial-overview',
    component: () => import('../views/FinancialOverviewView.vue'),
    meta: {
      requiresAuth: true,
      permission: 'manageUsers',
      title: 'Financial Overview',
    },
  },
  {
    path: '/no-access',
    name: 'no-access',
    component: () => import('../views/NoAccessView.vue'),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/dashboard',
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
})

router.beforeEach(async (to) => {
  const authStore = useAuthStore()
  const orgStore = useOrgStore()
  const isLoginRoute = to.path === '/login'
  const isChangePasswordRoute = to.path === '/change-password'
  const isNoAccessRoute = to.path === '/no-access'
  const requiresAuth = Boolean(to.meta?.requiresAuth)

  if (import.meta.env.DEV) {
    console.log('[ROUTER]', {
      step: 'ROUTE_START',
      to: to.fullPath,
      status: authStore.status,
      authStatus: authStore.isAuthenticated,
      hasToken: Boolean(authStore.token),
      hasUser: Boolean(authStore.user),
      hasRoles: Boolean(orgStore.activeOrgRole),
      rbacReady: authStore.rbacReady,
    })
  }

  // PHASE 0: DETECT_SESSION_INVALIDATION
  // If session was invalidated (org/suborg deleted or access revoked)
  // redirect to login with appropriate message
  if (authStore.status === 'session_invalidated') {
    if (import.meta.env.DEV) {
      console.log('[ROUTER]', {
        step: 'ROUTER_GUARD_SESSION_INVALIDATED',
        reason: authStore.sessionInvalidationReason,
      })
    }
    // Store the reason for display on login page
    sessionStorage.setItem('sessionInvalidationReason', authStore.sessionInvalidationReason)
    return '/login'
  }

  // PHASE 1: WAIT_BOOTSTRAP
  if (authStore.isAuthenticated && !authStore.rbacReady) {
    if (import.meta.env.DEV) {
      console.log('[ROUTER]', { step: 'ROUTER_GUARD_BOOTSTRAP_WAIT', to: to.fullPath })
    }

    const result = await authStore.initializeSession()
    if (!result?.success) {
      if (authStore.status === 'session_invalidated') {
        sessionStorage.setItem('sessionInvalidationReason', authStore.sessionInvalidationReason)
        return '/login'
      }
      authStore.logout()
      return '/login'
    }
  } else if (import.meta.env.DEV) {
    console.log('[ROUTER]', {
      step: 'ROUTER_GUARD_SKIP_BOOTSTRAP',
      reason: authStore.rbacReady ? 'already_ready' : 'no_token',
      to: to.fullPath,
    })
  }

  // PHASE 2: EVALUATE_AUTH
  if (!authStore.isAuthenticated && requiresAuth) {
    if (import.meta.env.DEV) {
      console.log('[ROUTER]', { step: 'ROUTER_GUARD_DECISION', decision: 'redirect', target: '/login', reason: 'not_authenticated' })
    }
    return '/login'
  }

  if (!authStore.isAuthenticated) {
    if (import.meta.env.DEV) {
      console.log('[ROUTER]', { step: 'ROUTER_GUARD_DECISION', decision: 'allow', reason: 'public_route' })
    }
    return true
  }

  if (authStore.isAuthenticated && isLoginRoute) {
    if (import.meta.env.DEV) {
      console.log('[ROUTER]', { step: 'ROUTER_GUARD_DECISION', decision: 'redirect', target: '/dashboard', reason: 'already_authenticated' })
    }
    return '/dashboard'
  }

  if (isNoAccessRoute) {
    if (import.meta.env.DEV) {
      console.log('[ROUTER]', { step: 'ROUTER_GUARD_DECISION', decision: 'allow', reason: 'no_access_route' })
    }
    return true
  }

  // PHASE 3: ENFORCE_PASSWORD_CHANGE
  if (authStore.isAuthenticated && authStore.user?.must_change_password === true) {
    if (!isChangePasswordRoute) {
      if (import.meta.env.DEV) {
        console.log('[ROUTER]', {
          step: 'ROUTER_GUARD_PASSWORD_CHANGE_REQUIRED',
          to: to.fullPath,
          target: '/change-password',
        })
      }
      return '/change-password'
    }

    if (import.meta.env.DEV) {
      console.log('[ROUTER]', {
        step: 'ROUTER_GUARD_PASSWORD_CHANGE_ALLOWED',
        to: to.fullPath,
      })
    }
    return true
  }

  // PHASE 3: LIFECYCLE_AWARE_CONTEXT_GUARD
  // Block navigation when currently selected org/suborg is soft-deleted.
  const bypassLifecycleGuard = to.meta?.bypassLifecycleGuard === true

  if (requiresAuth && !isNoAccessRoute && !bypassLifecycleGuard) {
    if (isActiveOrgInvalidated(orgStore.activeOrgId, orgStore.organizations)) {
      orgStore.setActiveOrg(null)
      return { path: '/no-access', query: { reason: 'org_deleted' } }
    }

    if (isActiveSubOrgInvalidated(orgStore.activeSubOrgId, orgStore.subOrgs)) {
      orgStore.setActiveSubOrg(null)
      return { path: '/no-access', query: { reason: 'suborg_deleted' } }
    }
  }

  // PHASE 4: EVALUATE_RBAC
  if (to.meta?.requiresSuperAdmin && !authStore.isSuperAdmin) {
    if (import.meta.env.DEV) {
      console.log('[ROUTER]', {
        step: 'ROUTER_GUARD_DECISION',
        decision: 'redirect',
        target: '/dashboard',
        reason: 'requires_superadmin',
      })
    }
    return '/dashboard'
  }


  if (to.meta?.permission) {
    try {
      assertPermission(authStore, orgStore, to.meta.permission, { scope: 'org' })
    } catch (err) {
      if (err?.code === RBAC_ERROR) {
        if (import.meta.env.DEV) {
          console.log('[ROUTER]', {
            step: 'ROUTER_GUARD_DECISION',
            decision: 'redirect',
            target: '/no-access',
            reason: 'rbac_denied',
            permission: to.meta.permission,
          })
        }
        return '/no-access'
      }
      throw err
    }
  }

  if (import.meta.env.DEV) {
    console.log('[ROUTER]', { step: 'ROUTER_GUARD_DECISION', decision: 'allow', reason: 'permission_granted' })
  }

  return true
})

router.afterEach((to) => {
  const appName = 'Lottri Admin'
  const pageTitle = String(to.meta?.title || '').trim()
  document.title = pageTitle ? `${pageTitle} | ${appName}` : appName
})

export default router

